(() => {
  if (customElements.get('collection-page')) return;

  const mqDesktop = window.matchMedia('(min-width: 760px)');
  const REGIONS = ['tabs', 'filter-count', 'sort', 'categories', 'filters', 'sheet-foot', 'results'];

  class CollectionPage extends HTMLElement {
    connectedCallback() {
      this.theme = window.theme || {};
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('[data-filter-form]');
      this.sidebar = this.querySelector('[data-filter-sidebar]');
      this.sheet = this.querySelector('[data-filter-sheet]');
      this.sheetBody = this.querySelector('[data-sheet-body]');
      this.path = window.location.pathname;

      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onOverlay = this.onOverlay.bind(this);
      this.onMedia = this.onMedia.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      window.addEventListener('popstate', this.onPopState);
      document.addEventListener('overlay:change', this.onOverlay);
      mqDesktop.addEventListener('change', this.onMedia);

      if (!window.Shopify?.designMode) {
        window.history.replaceState({ ...(window.history.state || {}), collectionPage: true }, '', window.location.href);
      }
      this.refreshCounts();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      document.removeEventListener('overlay:change', this.onOverlay);
      mqDesktop.removeEventListener('change', this.onMedia);
      this.controller?.abort();
      this.countsController?.abort();
    }

    params({ keepSort = true } = {}) {
      const params = new URLSearchParams();
      const range = this.form.querySelector('[data-price-range]');
      const select = this.querySelector('select[name="sort_by"]');
      for (const [key, value] of new FormData(this.form)) {
        if (value === '') continue;
        if (range && key === range.name && Number(value) >= Number(range.max)) continue;
        if (key === 'sort_by' && (!keepSort || value === select?.dataset.defaultSort)) continue;
        params.append(key, value);
      }
      return params;
    }

    buildUrl() {
      const query = this.params().toString();
      return query ? `${this.path}?${query}` : this.path;
    }

    onChange(event) {
      const target = event.target;
      clearTimeout(this.rangeTimer);
      if (target.matches('[data-category-url]')) {
        this.navigate(target.dataset.categoryUrl);
        return;
      }
      if (target.form !== this.form) return;
      if (target.matches('[data-price-range]')) {
        this.rangeTimer = setTimeout(() => this.navigate(this.buildUrl()), 300);
        return;
      }
      this.navigate(this.buildUrl());
    }

    onInput(event) {
      if (event.target.matches?.('[data-price-range]')) this.updatePrice(event.target);
    }

    onClick(event) {
      const link = event.target.closest('a[data-collection-link]');
      if (link) {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (link.getAttribute('aria-disabled') === 'true' || !link.hasAttribute('href')) return;
        this.navigate(link.href, { scroll: link.hasAttribute('data-scroll-results') ? 'results' : 'auto' });
        return;
      }

      const clear = event.target.closest('[data-collection-clear]');
      if (clear) {
        event.preventDefault();
        this.navigate(clear.dataset.url);
        return;
      }

      const more = event.target.closest('[data-read-more]');
      if (more) {
        const panel = document.getElementById(more.getAttribute('aria-controls'));
        const open = more.getAttribute('aria-expanded') !== 'true';
        more.setAttribute('aria-expanded', String(open));
        more.textContent = open ? more.dataset.labelLess : more.dataset.labelMore;
        if (panel) panel.hidden = !open;
      }
    }

    onSubmit(event) {
      if (event.target !== this.form) return;
      event.preventDefault();
      this.navigate(this.buildUrl());
    }

    onPopState(event) {
      if (!event.state?.collectionPage) return;
      this.navigate(window.location.href, { push: false, scroll: 'none' });
    }

    onOverlay(event) {
      const { overlay, open } = event.detail || {};
      if (overlay !== this.sheet) return;
      if (open) this.sheetBody.append(this.form);
      else this.sidebar.append(this.form);
    }

    onMedia() {
      if (!mqDesktop.matches) return;
      if (!this.sheet.hidden) this.theme.closeOverlay?.(this.sheet, { restoreFocus: false });
      if (this.form.parentElement !== this.sidebar) this.sidebar.append(this.form);
    }

    updatePrice(range) {
      const value = Number(range.value);
      const max = Number(range.max) || 1;
      const amount = (range.dataset.moneyFormat || '[amount]').replace(/\{\{\s*\w+\s*\}\}|\[amount\]/, String(value));
      const label = value >= max ? range.dataset.labelAny : range.dataset.labelUpTo.replace('[price]', amount);
      range.setAttribute('aria-valuetext', label);
      const group = range.closest('.filter-group');
      const output = group?.querySelector('[data-price-label]');
      if (output) output.textContent = label;
      range.closest('[data-price-track]')?.style.setProperty('--fill', `${(value / max) * 100}%`);
    }

    setBusy(busy) {
      this.toggleAttribute('data-busy', busy);
      this.querySelector('[data-results]')?.setAttribute('aria-busy', String(busy));
    }

    async navigate(url, { push = true, scroll = 'auto' } = {}) {
      const target = new URL(url, window.location.origin);
      target.searchParams.delete('section_id');
      const request = new URL(target.href);
      request.searchParams.set('section_id', this.sectionId);

      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.setBusy(true);

      try {
        const response = await fetch(request, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.querySelector('collection-page');
        if (!fresh || fresh.dataset.sectionId !== this.sectionId) throw new Error('Section not found');

        const pathChanged = target.pathname !== this.path;
        this.swap(fresh, pathChanged);
        this.path = target.pathname;
        this.dataset.filtered = fresh.dataset.filtered;
        this.dataset.resultsLabel = fresh.dataset.resultsLabel;
        if (push) window.history.pushState({ collectionPage: true }, '', `${target.pathname}${target.search}`);
        if (fresh.dataset.title) document.title = fresh.dataset.title;
        this.theme.announce?.(fresh.dataset.resultsLabel);
        this.theme.initDeferredImages?.(this);
        this.scrollAfter(scroll);
        this.refreshCounts();
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.assign(target.href);
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          this.setBusy(false);
        }
      }
    }

    swap(fresh, pathChanged) {
      const active = document.activeElement;
      const activeId = active && this.contains(active) ? active.id : '';
      const activeRegion = active && this.contains(active) ? active.closest('[data-region]')?.dataset.region : '';
      const sheetScroll = this.sheetBody.scrollTop;
      const sidebarScroll = this.sidebar.scrollTop;
      const names = pathChanged ? [...REGIONS, 'head'] : REGIONS;

      names.forEach((name) => {
        const current = this.querySelector(`[data-region="${name}"]`);
        const next = fresh.querySelector(`[data-region="${name}"]`);
        if (current && next) current.replaceChildren(...Array.from(next.childNodes));
      });

      const shell = this.querySelector('.collection-shell');
      const freshShell = fresh.querySelector('.collection-shell');
      if (shell && freshShell) shell.className = freshShell.className;

      this.sheetBody.scrollTop = sheetScroll;
      this.sidebar.scrollTop = sidebarScroll;

      if (!activeRegion && !activeId) return;
      const again = activeId ? document.getElementById(activeId) : null;
      if (again && !again.disabled && again.getClientRects().length) {
        again.focus({ preventScroll: true });
      } else if (again?.closest('label')) {
        again.closest('[role="group"], [role="radiogroup"]')?.querySelector('input:not(:disabled)')?.focus({ preventScroll: true });
      } else if (activeRegion === 'results' || activeRegion === 'tabs' || activeRegion === 'filters') {
        this.querySelector('[data-results-heading]')?.focus({ preventScroll: true });
      }
    }

    scrollAfter(mode) {
      if (mode === 'none') return;
      const anchor = this.querySelector('[data-results]');
      if (!anchor) return;
      const top = anchor.getBoundingClientRect().top;
      if (mode === 'auto' && top >= 0) return;
      const toolbar = this.querySelector('[data-collection-toolbar]');
      const header = document.querySelector('.header-section');
      const offset = (toolbar?.offsetHeight || 0) + (header?.offsetHeight || 0) + 12;
      const reduce = this.theme.motion?.() === 'off';
      window.scrollTo({ top: Math.max(window.scrollY + top - offset, 0), behavior: reduce ? 'auto' : 'smooth' });
    }

    async refreshCounts() {
      if (this.dataset.filtered !== 'true') return;
      const holders = Array.from(this.querySelectorAll('[data-count-handle]'));
      const handles = [...new Set(holders.map((node) => node.dataset.countHandle))];
      if (!handles.length) return;

      const params = this.params({ keepSort: false });
      params.set('section_id', 'collection-count');
      const root = this.theme.routes?.root || '/';

      this.countsController?.abort();
      const controller = new AbortController();
      this.countsController = controller;

      try {
        const counts = await Promise.all(
          handles.map(async (handle) => {
            const response = await fetch(`${root}collections/${handle}?${params}`, { signal: controller.signal });
            if (!response.ok) return null;
            const match = (await response.text()).match(/data-collection-count[^>]*>\s*(\d+)/);
            return match ? Number(match[1]) : null;
          })
        );
        if (this.countsController !== controller) return;
        handles.forEach((handle, index) => {
          if (counts[index] == null) return;
          holders
            .filter((node) => node.dataset.countHandle === handle && node.isConnected)
            .forEach((node) => this.setCount(node, counts[index]));
        });
      } catch (error) {
        if (error.name !== 'AbortError') this.countsController = null;
      }
    }

    setCount(node, count) {
      const output = node.querySelector('[data-count]');
      if (output) output.textContent = String(count);
      const empty = count === 0;

      if (node.tagName === 'A') {
        const current = node.getAttribute('aria-current') === 'page';
        if (empty && !current) {
          if (node.hasAttribute('href')) node.dataset.href = node.getAttribute('href');
          node.removeAttribute('href');
          node.setAttribute('aria-disabled', 'true');
        } else if (node.dataset.href) {
          node.setAttribute('href', node.dataset.href);
          node.removeAttribute('aria-disabled');
        }
        return;
      }

      const input = node.querySelector('input');
      if (input) input.disabled = empty && !input.checked;
    }
  }

  customElements.define('collection-page', CollectionPage);
})();
